import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Grid, Box, Button } from '@mui/material';
import { GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { generateSelectMenuItems } from 'src/utils';
import { networkCreateLandingPage, networkUpdateLandingPage, useFetchLandingPageCategoryList } from 'src/network/landing';
import { useQueryClient } from '@tanstack/react-query';
import useFormsModalStore from 'src/store/forms';
import { LANDING_PAGE_LIST } from 'src/constants/queryKeys';

const LandingPageForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  const [landingCategories, setLandingCategories] = useState([]);
  const [selectedLandingCategory, setSelectedLandingCategory] = useState('');
  const landingDetails = modal?.data;
  const defaultValues = getDefaultValues(landingDetails);
  const isEditMode = landingDetails;

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      LandPageLandPageCategoryID: selectedLandingCategory,
      LandPageIsShowTextInfo: values?.LandPageIsShowTextInfo ? 1 : 0,
      LandPageIsShowButton: values?.LandPageIsShowButton ? 1 : 0,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateLandingPage(landingDetails?.LandPageID, PAYLOAD);
        if (res?.data?.items?.ResultID !== -1) {
          enqueueSnackbar('لندینگ پیج موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
        } else {
          enqueueSnackbar('خطایی در ویرایش لندینگ پیج رخ داد!', { variant: 'error' });
        }
      } else {
        const res = await networkCreateLandingPage(PAYLOAD);
        if (res?.data?.items?.ResultID !== -1) {
          enqueueSnackbar('لندینگ پیج موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          setSelectedLandingCategory(landingCategories[0]?.id);
        } else {
          enqueueSnackbar('خطایی در ایجاد لندینگ پیج رخ داد!', { variant: 'error' });
        }
      }
    
      await queryClient.invalidateQueries({ queryKey: [LANDING_PAGE_LIST] });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('خطای ناشناخته رخ داد!', { variant: 'error' });
    }
    
  };

  const { data } = useFetchLandingPageCategoryList();

  useEffect(() => {
    if (data) {
      const result = data?.data?.items || [];
      const categories = generateSelectMenuItems(result, 'LandPageCategoryLable', 'LandPageCategoryID');
      setLandingCategories(categories);
      setSelectedLandingCategory(categories?.LandPageCategoryID || categories?.[0]?.id);
    }
  }, [data]);

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField name="LandPageTitle" type="text" label="عنوان" />
                <RHFTextField name="LandPageSubTitle" type="text" label="زیر عنوان" />

                <RHFTextField name="LandPageLink" type="text" label="لینک" />
                <RHFTextField name="LandPageLinkText" type="text" label="متن لینک" />

                <GeneralUpload
                  label="تصویر دسکتاپ"
                  id="landـpage_image_desktop"
                  name="LandPageImageDesktop"
                  afterChangeAction={(res) => {
                    setValue('LandPageImageDesktop', res);
                    trigger('LandPageImageDesktop');
                  }}
                />

                <GeneralUpload
                  label="تصویر موبایل (حالت افقی)"
                  id="landـpage_image_mobile_h"
                  name="LandPageImageMobileH"
                  afterChangeAction={(res) => {
                    setValue('LandPageImageMobileH', res);
                    trigger('LandPageImageMobileH');
                  }}
                />

                <GeneralUpload
                  label="تصویر موبایل (حالت عمودی)"
                  id="landـpage_image_mobile_v"
                  name="LandPageImageMobileV"
                  afterChangeAction={(res) => {
                    setValue('LandPageImageMobileV', res);
                    trigger('LandPageImageMobileV');
                  }}
                />

                <GeneralSelectMenu
                  label="دسته بندی"
                  name="LandPageLandPageCategoryID"
                  options={landingCategories}
                  value={selectedLandingCategory}
                  onChangeValue={setSelectedLandingCategory}
                />
              </Box>

              <Box
                sx={{
                  mt: 3,
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)' },
                }}
              >
                <RHFTextField name="LandPageDescription" multiline rows={6} label="توضیحات" />
              </Box>

              <Box
                sx={{
                  mt: 3,
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(3, 1fr)' },
                }}
              >
                <RHFSwitch name="LandPageIsShowTextInfo" labelPlacement="end" label="نمایش متن" />
                <RHFSwitch name="LandPageIsShowButton" labelPlacement="end" label="نمایش دکمه" />
              </Box>

              <div className="flex items-center gap-4 mt-6">
                <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                  ذخیره
                </LoadingButton>
                <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                  {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                </LoadingButton>
                <Button onClick={closeModal} variant="contained" color="error">
                  انصراف
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};

export default LandingPageForm;
